import { observer } from 'mobx-react'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { Customer, Payout as IPayout, ManagedBalanceLedger } from '../state/Radom'
import { StatusBadge } from './CheckoutSessions'
import _, { camelCase } from 'lodash'
import {
  errorToast,
  shortAddress,
  roundAssetAmount,
  successToast,
  trimZeros,
  unifyLedger,
  useTestMode
} from '../util/Util'
import TimeText from '../components/TimeText'
import Table from '../components/Table'
import { IconButton } from '../components/Button'
import Copy from '../icons/Copy'
import { ExternalLinkText } from './Events'
import { formatExplorerUrl } from '../util/Chains'
import LinkExternal from '../components/LinkExternal'
import { getMethod, ManagedPaymentMethod, prodNetworks, testNetworks } from '../util/Managed'
import { Section, SectionContent, SectionItem, SubTitle } from '../components/Shared'
import Modal from '../components/Modal'
import ExportPayoutEntriesForm from '../components/ExportPayoutEntriesForm'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import {
  linkExternalIconStyle,
  loadingWrapperStyle,
  containerStyle,
  headerRowStyle,
  dropdownContentStyle,
  payoutHeaderStyle,
  payoutLogoStyle,
  tickerBadgeStyle,
  feeRowStyle,
  feeLogoStyle,
  ledgerTitleStyle,
  ledgerRowStyle,
  ledgerMutedTextStyle,
  withdrawalTitleStyle,
  payoutAddressCellStyle,
  iconButtonNoPaddingStyle,
  copyIconStyle,
  txLinkStyle
} from './PayoutStyles'

const Payout = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')

  const [payout, setPayout] = useState<IPayout>()
  const [isLoading, setIsLoading] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>()
  const [exportPayoutEntriesModalOpen, setExportPayoutEntriesModalOpen] = useState(false)
  const [testMode] = useTestMode()

  const copyToClipboard = useCallback(
    async (text: string, successMsg: string, failureMsg: string) => {
      try {
        await navigator.clipboard.writeText(text)
        successToast(successMsg)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('copy failed', err)
        errorToast(failureMsg)
      }
    },
    []
  )

  const fetchPayoutData = useCallback(
    async (id: string): Promise<void> => {
      setIsLoading(true)

      let payoutResp: IPayout
      try {
        payoutResp = await Radom.getPayout(id)
        setPayout(payoutResp)

        if (payoutResp.payouts[0].payoutType.crypto) {
          setPaymentMethod(
            getMethod(
              payoutResp.payouts[0].payoutType.crypto.network,
              payoutResp.payouts[0].payoutType.crypto.token
            )
          )
        }
      } catch {
        setIsLoading(false)
        errorToast('Failed to load payout')
        return
      }

      try {
        const ledger = await Radom.listManagedBalanceLedger([payoutResp.id])
        if (ledger.length === 1) {
          setManagedBalanceLedger(ledger)
        } else {
          setManagedBalanceLedger(unifyLedger(ledger))
        }
      } catch {
        errorToast('Failed to load ledger')
      }

      const ids = Array.from(
        new Set(
          payoutResp.payouts
            .map(it => it.payoutType.crypto?.customerId)
            .filter((id): id is string => Boolean(id))
        )
      )

      const needed = new Set(ids)
      const found: Customer[] = []
      const limit = 20
      const parallelQueries = 3
      const networks = testMode ? testNetworks : prodNetworks
      const sortType = 'createdAtDescending'

      try {
        const firstPage = await Radom.listCustomers(0, limit, networks, sortType)
        const total = firstPage.total

        for (const c of firstPage.data) {
          if (needed.has(c.id)) {
            found.push(c)
            needed.delete(c.id)
            if (needed.size === 0) break
          }
        }

        if (total >= limit && needed.size > 0) {
          for (
            let baseOffset = limit;
            baseOffset < total && needed.size > 0;
            baseOffset += limit * parallelQueries
          ) {
            const offsets: number[] = []
            for (let k = 0; k < parallelQueries; k++) {
              const off = baseOffset + k * limit
              if (off < total) offsets.push(off)
            }

            const pages = await Promise.all(
              offsets.map(async off => await Radom.listCustomers(off, limit, networks, sortType))
            )

            for (const page of pages) {
              for (const c of page.data) {
                if (needed.has(c.id)) {
                  found.push(c)
                  needed.delete(c.id)
                  if (needed.size === 0) break
                }
              }
              if (needed.size === 0) break
            }
          }
        }

        setCustomers(found)
      } catch {
        errorToast('Failed to load customers')
      }

      setIsLoading(false)
    },
    [testMode]
  )

  const debouncedFetchPayoutData = useMemo(() => _.debounce(fetchPayoutData, 300), [fetchPayoutData])

  const handleCloseExportPayoutEntriesModal = useCallback(() => {
    setExportPayoutEntriesModalOpen(false)
  }, [])

  const handleOpenExportPayoutEntriesModal = useCallback(() => {
    setExportPayoutEntriesModalOpen(true)
  }, [])

  useEffect(() => {
    if (!params.id) return
    debouncedFetchPayoutData(params.id)
    return () => debouncedFetchPayoutData.cancel()
  }, [params.id, debouncedFetchPayoutData])

  if (isLoading) {
    return (
      <div style={loadingWrapperStyle}>
        <Spinner />
      </div>
    )
  }

  if (payout && paymentMethod) {
    return (
      <Container style={containerStyle}>
        <div style={headerRowStyle}>
          <BreadCrumbs
            items={
              source === 'transactions'
                ? [
                  { item: 'Transactions', to: '/transactions' },
                  { item: payout.id, to: `/payout/${payout.id}?source=transactions` }
                ]
                : [
                  { item: 'Payouts', to: '/payouts' },
                  { item: payout.id, to: '.' }
                ]
            }
          />

          <Dropdown
            noChevron
            selectedContent={<Dots width={15} />}
            dropdownContent={
              <div style={dropdownContentStyle}>
                <DropdownItem onClick={handleOpenExportPayoutEntriesModal}>
                  Export payout entries
                </DropdownItem>
              </div>
            }
          />
        </div>

        <Section>
          <div style={payoutHeaderStyle}>
            <img src={paymentMethod.logo} style={payoutLogoStyle} />
            <span>{trimZeros(payout.totalPayoutUsd)}</span>
            <span style={tickerBadgeStyle}>{paymentMethod.ticker}</span>
          </div>

          <SectionContent>
            <SectionItem>
              <SubTitle>Network fee</SubTitle>
              <div style={feeRowStyle}>
                <img src={paymentMethod.logo} style={feeLogoStyle} />
                <span>{trimZeros(payout.estimatedFeeUsd)}</span>
              </div>
            </SectionItem>

            <SectionItem>
              <SubTitle>Status</SubTitle>
              <StatusBadge
                status={
                  camelCase(payout.status) as
                    | 'awaitingConfirmation'
                    | 'pending'
                    | 'complete'
                    | 'expired'
                    | 'processing'
                }
              >
                {payout.status}
              </StatusBadge>
            </SectionItem>

            <SectionItem>
              <SubTitle>Deduct fees</SubTitle>
              <span>{payout.deductFees ? 'Yes' : 'No'}</span>
            </SectionItem>

            <SectionItem>
              <SubTitle>Email receipt</SubTitle>
              <span>{payout.sendEmailReceipts ? 'Yes' : 'No'}</span>
            </SectionItem>

            <SectionItem>
              <SubTitle>Created</SubTitle>
              <TimeText date={new Date(payout.createdAt)} />
            </SectionItem>

            <SectionItem>
              <SubTitle>Completed</SubTitle>
              <TimeText date={new Date(payout.completedAt)} />
            </SectionItem>
          </SectionContent>
        </Section>

        {managedBalanceLedger && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Ledger</SubTitle>

            {managedBalanceLedger.map((item, idx) => {
              const method = getMethod(item.network, item.token)

              return (
                <SectionContent key={idx}>
                  <SectionItem>
                    <SubTitle>Balance Before</SubTitle>
                    <div style={ledgerRowStyle}>
                      <span>{roundAssetAmount(method.ticker, Number(item.balanceBefore))}</span>
                      <span style={ledgerMutedTextStyle}>
                        {method.ticker ?? method.logo} on {item.network}
                      </span>
                    </div>
                  </SectionItem>

                  <SectionItem>
                    <SubTitle>Balance After</SubTitle>
                    <div style={ledgerRowStyle}>
                      <span>{roundAssetAmount(method.ticker, Number(item.balanceAfter))}</span>
                      <span style={ledgerMutedTextStyle}>
                        {method.ticker ?? method.logo} on {item.network}
                      </span>
                    </div>
                  </SectionItem>
                </SectionContent>
              )
            })}
          </Section>
        )}

        <Section>
          <SubTitle style={withdrawalTitleStyle}>Withdrawal transactions</SubTitle>

          <Table
            disableHoverStyle
            headers={[
              <span key="address">Payout address</span>,
              <span key="amount">Amount</span>,
              <span key="network">Network</span>,
              <span key="name">Beneficiary Name</span>,
              <span key="email">Beneficiary Email</span>,
              <span key="hash">Transaction Hash</span>
            ]}
            items={payout.payouts.map((item, i) => {
              const customer = customers.find(c => c.id === item.payoutType.crypto?.customerId)
              const hasName = Boolean(customer?.name && customer.name.length !== 0 && customer.name !== 'null null')
              const hasEmail = Boolean(customer?.email && customer.email.length)

              const payoutAddress = item.payoutType.crypto?.payoutAddress ?? ''
              const txHash = item.payoutEntryReceipt?.crypto?.txHash ?? ''

              return [
                <div key={`${i}-a`} style={payoutAddressCellStyle}>
                  <span>{shortAddress(payoutAddress, 8)}</span>
                  <IconButton style={iconButtonNoPaddingStyle}>
                    <Copy
                      style={copyIconStyle}
                      onClick={e => {
                        e.stopPropagation()
                        void copyToClipboard(payoutAddress, 'Copied payout address', 'Failed to copy payout address')
                      }}
                    />
                  </IconButton>
                </div>,

                <div key={`${i}-b`}>
                  <div style={feeRowStyle}>
                    <img src={paymentMethod.logo} style={feeLogoStyle} />
                    <span>{trimZeros(item.payoutType.crypto?.amount || '')}</span>
                  </div>
                </div>,

                <div key={`${i}-c`}>
                  <div style={feeRowStyle}>
                    <img
                      src={paymentMethod.hostChain?.logo ?? paymentMethod.logo}
                      style={feeLogoStyle}
                    />
                    <span>{paymentMethod.hostChain?.name ?? paymentMethod.name}</span>
                  </div>
                </div>,

                <div key={`${i}-d`}>
                  <div style={feeRowStyle}>
                    <span>{hasName ? customer?.name : '-'}</span>
                    {hasName && (
                      <IconButton style={iconButtonNoPaddingStyle}>
                        <Copy
                          style={copyIconStyle}
                          onClick={e => {
                            e.stopPropagation()
                            void copyToClipboard(
                              customer?.name ?? '',
                              'Copied customer name',
                              'Failed to copy customer name'
                            )
                          }}
                        />
                      </IconButton>
                    )}
                  </div>
                </div>,

                <div key={`${i}-e`}>
                  <div style={feeRowStyle}>
                    <span>{hasEmail ? customer?.email : '-'}</span>
                    {hasEmail && (
                      <IconButton style={iconButtonNoPaddingStyle}>
                        <Copy
                          style={copyIconStyle}
                          onClick={e => {
                            e.stopPropagation()
                            void copyToClipboard(
                              customer?.email ?? '',
                              'Copied customer email',
                              'Failed to copy customer email'
                            )
                          }}
                        />
                      </IconButton>
                    )}
                  </div>
                </div>,

                <div key={`${i}-f`}>
                  <ExternalLinkText
                    style={txLinkStyle}
                    onClick={e => e.stopPropagation()}
                    target="_blank"
                    rel="noreferrer"
                    href={formatExplorerUrl(item.payoutType.crypto?.network || '', 'tx', txHash)}
                  >
                    {shortAddress(txHash, 8)}
                    <LinkExternal stroke={linkExternalIconStyle.stroke} width={linkExternalIconStyle.width} />
                  </ExternalLinkText>
                </div>
              ]
            })}
            isLoading={false}
          />
        </Section>

        {exportPayoutEntriesModalOpen && (
          <Modal
            visible={exportPayoutEntriesModalOpen}
            onClose={handleCloseExportPayoutEntriesModal}
            title="Export payout entries"
          >
            <ExportPayoutEntriesForm id={payout.id} />
          </Modal>
        )}
      </Container>
    )
  }

  return (
    <div>
      <span>Payout not found</span>
    </div>
  )
})

export default Payout
